import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { ShieldCheck, Loader2, AlertCircle } from "lucide-react";
import { toast } from "sonner";

export default function Admin2FA() {
    const [status, setStatus] = useState(null);
    const [setup, setSetup] = useState(null);
    const [code, setCode] = useState("");
    const [busy, setBusy] = useState(false);

    const refresh = () => api.get("/admin/2fa/status").then((r) => setStatus(r.data));
    useEffect(() => { refresh(); }, []);

    const startSetup = async () => {
        setBusy(true);
        try {
            const { data } = await api.post("/admin/2fa/setup");
            setSetup(data);
        } catch (e) {
            toast.error(e?.response?.data?.detail || "Erro");
        } finally {
            setBusy(false);
        }
    };

    const enable = async () => {
        if (!/^\d{6}$/.test(code)) { toast.error("Código deve ter 6 dígitos"); return; }
        setBusy(true);
        try {
            await api.post("/admin/2fa/enable", { code });
            toast.success("2FA activado!");
            setSetup(null);
            setCode("");
            refresh();
        } catch (e) {
            toast.error(e?.response?.data?.detail || "Código inválido");
        } finally {
            setBusy(false);
        }
    };

    const disable = async () => {
        if (!/^\d{6}$/.test(code)) { toast.error("Insira código actual para confirmar"); return; }
        if (!window.confirm("Desactivar 2FA? A sua conta ficará menos segura.")) return;
        setBusy(true);
        try {
            await api.post("/admin/2fa/disable", { code });
            toast.success("2FA desactivado");
            setCode("");
            refresh();
        } catch (e) {
            toast.error(e?.response?.data?.detail || "Erro");
        } finally {
            setBusy(false);
        }
    };

    return (
        <div data-testid="admin-2fa-page">
            <div className="mb-6">
                <div className="label-mono text-magenta">Segurança</div>
                <h1 className="font-display font-black text-3xl">Autenticação 2 Factores</h1>
            </div>

            <div className="border-2 border-ink bg-white p-6 max-w-xl">
                {status === null ? <Loader2 className="animate-spin" /> : status.enabled ? (
                    <div data-testid="2fa-enabled-state">
                        <div className="flex items-center gap-3 mb-5 p-4 border-2 border-accent-green bg-accent-green/5">
                            <ShieldCheck className="text-accent-green" />
                            <div>
                                <div className="font-bold">2FA está activo</div>
                                <div className="text-sm text-ink/60 font-mono">A sua conta requer código TOTP no login.</div>
                            </div>
                        </div>
                        <p className="font-mono text-sm mb-3">Para desactivar, insira o código actual:</p>
                        <input type="text" inputMode="numeric" maxLength={6} value={code} onChange={(e) => setCode(e.target.value.replace(/\D/g, ""))}
                            className="input-edge text-2xl font-mono tracking-widest text-center mb-4" placeholder="000000"
                            data-testid="disable-totp-input" />
                        <button onClick={disable} disabled={busy} className="border-2 border-red-600 text-red-600 px-6 py-3 font-bold uppercase tracking-wider text-sm hover:bg-red-600 hover:text-white transition-colors w-full disabled:opacity-50" data-testid="disable-2fa-btn">
                            {busy ? <Loader2 className="animate-spin inline" size={16} /> : "Desactivar 2FA"}
                        </button>
                    </div>
                ) : setup ? (
                    <div data-testid="2fa-setup-state">
                        <div className="flex items-start gap-3 mb-5 p-4 border-2 border-magenta bg-magenta/5">
                            <AlertCircle className="text-magenta flex-shrink-0" />
                            <div className="text-sm">
                                <div className="font-bold">Passo 1: Adicione ao Authenticator</div>
                                <p className="text-ink/70 font-mono text-xs mt-1">
                                    Abra Google Authenticator ou Authy e digitalize o QR code abaixo.
                                </p>
                            </div>
                        </div>
                        <div className="bg-paper p-6 border-2 border-ink mb-5 text-center">
                            <img src={setup.qr_data_url} alt="QR code 2FA" className="mx-auto" data-testid="totp-qr" />
                            <div className="mt-4 font-mono text-xs text-ink/60">
                                Ou insira manualmente: <code className="bg-white px-2 py-1 select-all">{setup.secret}</code>
                            </div>
                        </div>
                        <p className="font-mono text-sm mb-3">Passo 2: Insira o código de 6 dígitos:</p>
                        <input type="text" inputMode="numeric" maxLength={6} value={code} onChange={(e) => setCode(e.target.value.replace(/\D/g, ""))}
                            className="input-edge text-2xl font-mono tracking-widest text-center mb-4" placeholder="000000"
                            autoFocus data-testid="enable-totp-input" />
                        <button onClick={enable} disabled={busy} className="btn-primary w-full disabled:opacity-50" data-testid="enable-2fa-btn">
                            {busy ? <Loader2 className="animate-spin" size={16} /> : "Activar 2FA"}
                        </button>
                    </div>
                ) : (
                    <div data-testid="2fa-disabled-state">
                        <p className="mb-4">Adicione uma camada extra de segurança à sua conta admin com autenticação de 2 factores.</p>
                        <ul className="font-mono text-sm space-y-1 mb-5 text-ink/70">
                            <li>✓ Compatível com Google Authenticator e Authy</li>
                            <li>✓ Código de 6 dígitos no login</li>
                            <li>✓ Pode desactivar a qualquer momento</li>
                        </ul>
                        <button onClick={startSetup} disabled={busy} className="btn-primary" data-testid="start-2fa-btn">
                            {busy ? <Loader2 className="animate-spin" size={16} /> : <ShieldCheck size={16} />}
                            Activar 2FA
                        </button>
                    </div>
                )}
            </div>
        </div>
    );
}
